/**
 * custom-product-card-choice.js
 * 「自分でチョイスセット」コレクションページ用 選択・カート追加処理
 */

(() => {
  // -----------------------------------------------------------------------
  // 定数
  // -----------------------------------------------------------------------
  const SESSION_KEY = "choiceSelections";
  const SNAPSHOT_KEY = "choiceSelectionsSnapshot"; // カート追加直前のスナップショット

  const CATEGORY_MAP = {
    主菜: "主菜",
    副主菜: "副主菜",
    副菜: "副菜",
    "おやつ・デザート": "おやつ",
  };

  // -----------------------------------------------------------------------
  // sessionStorage ユーティリティ
  // -----------------------------------------------------------------------
  function loadSelections() {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    } catch {
      return {};
    }
  }

  function saveSelections(selections) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(selections));
  }

  // -----------------------------------------------------------------------
  // 価格フォーマット（Shopify最小単位 = 円×100 → 円）
  // -----------------------------------------------------------------------
  function formatPrice(cents) {
    return "¥" + Math.floor(cents / 100).toLocaleString("ja-JP");
  }

  // -----------------------------------------------------------------------
  // カート内金額を /cart.js から取得（cents単位で返す）
  // -----------------------------------------------------------------------
  async function fetchCartTotalCents() {
    try {
      const res = await fetch("/cart.js");
      const data = await res.json();
      return data.total_price ?? 0;
    } catch {
      return 0;
    }
  }

  // -----------------------------------------------------------------------
  // 選択中商品の合計金額を計算（cents単位）
  // -----------------------------------------------------------------------
  function calcSelectedTotalCents(selections) {
    return Object.values(selections).reduce((sum, item) => {
      return sum + item.price * item.quantity;
    }, 0);
  }

  // -----------------------------------------------------------------------
  // 固定エリアのUI更新（全同クラス要素を一括更新）
  // -----------------------------------------------------------------------
  async function updateFixedArea(selections) {
    const fixedArea = document.querySelector(".c-choice__fixed-area");
    if (!fixedArea) return;

    const minimumCents = parseInt(fixedArea.dataset.minimumAmount, 10) || 0;
    const items = Object.values(selections);
    const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const selectedCents = calcSelectedTotalCents(selections);
    const cartCents = await fetchCartTotalCents();
    const totalCents = selectedCents + cartCents;
    const remainCents = Math.max(minimumCents - totalCents, 0);
    const reached = totalCents >= minimumCents;
    const hasSelection = items.length > 0;

    // 品数
    document
      .querySelectorAll(".js-choice__selected-item-count")
      .forEach((el) => {
        el.textContent = totalCount;
      });

    // 選択中合計金額
    document
      .querySelectorAll(".js-choice__selected-item-amount")
      .forEach((el) => {
        el.textContent = formatPrice(selectedCents);
      });

    // カート内金額
    document.querySelectorAll(".js-choice__cart-amount").forEach((el) => {
      el.textContent = formatPrice(cartCents);
    });

    // 追加後合計金額
    document.querySelectorAll(".js-choice__total-amount").forEach((el) => {
      el.textContent = formatPrice(totalCents);
    });

    // 残り金額
    document.querySelectorAll(".js-choice__remaining-amount").forEach((el) => {
      el.textContent = Math.floor(remainCents / 100).toLocaleString("ja-JP");
    });

    // 達成メッセージ切り替え
    document
      .querySelectorAll(".js-choice__remaining-amount-wrap")
      .forEach((el) => {
        el.hidden = reached;
      });
    document.querySelectorAll(".js-choice__reached-message").forEach((el) => {
      el.hidden = !reached;
    });

    // インジケーターバー
    const pct =
      minimumCents > 0 ? Math.min((totalCents / minimumCents) * 100, 100) : 0;
    document
      .querySelectorAll(".c-choice__fixed-indicator-bar")
      .forEach((bar) => {
        bar.style.width = pct + "%";
        bar.classList.toggle("is-reached", reached);
      });

    // カートに追加ボタン
    document.querySelectorAll(".js-choice-cart-button").forEach((btn) => {
      btn.disabled = !hasSelection;
    });
    document.querySelectorAll(".js-choice-cart-button__text").forEach((el) => {
      el.textContent = hasSelection
        ? "カートに追加する"
        : "商品を選択してください";
    });
  }

  // -----------------------------------------------------------------------
  // カテゴリ別カウント更新
  // -----------------------------------------------------------------------
  function updateCategoryStatus(selections) {
    const counts = { 主菜: 0, 副主菜: 0, 副菜: 0, おやつ: 0 };

    Object.values(selections).forEach((item) => {
      item.categories.forEach((rawCat) => {
        const displayCat = CATEGORY_MAP[rawCat];
        if (displayCat && counts[displayCat] !== undefined) {
          counts[displayCat] += item.quantity;
        }
      });
    });

    document
      .querySelectorAll(".c-selection-status__item-number[data-category]")
      .forEach((el) => {
        const cat = el.dataset.category;
        const numEl = el.querySelector("span");
        if (numEl) {
          numEl.textContent = counts[cat] ?? 0;
        } else {
          el.textContent = (counts[cat] ?? 0) + "品";
        }
      });
  }

  // -----------------------------------------------------------------------
  // 「選択する」ボタン状態の同期（カード上のボタン）
  // -----------------------------------------------------------------------
  function syncSelectButtons(selections) {
    document
      .querySelectorAll(".c-product-card__select-button[data-variant-id]")
      .forEach((btn) => {
        const variantId = btn.dataset.variantId;
        const isSelected = !!selections[variantId];
        btn.classList.toggle("is-selected", isSelected);
        // カード(li)にも選択状態を反映し、黄色い太ボーダーで強調する（横田指示）
        const card = btn.closest(".js-choice-card");
        if (card) card.classList.toggle("is-selected", isSelected);
        const textEl = btn.querySelector(".c-product-card__select-button-text");
        if (textEl && !btn.disabled) {
          textEl.textContent = isSelected ? "選択済み" : "選択する";
        }
      });
  }

  // -----------------------------------------------------------------------
  // 選択中メニューの1アイテムHTMLを生成
  // -----------------------------------------------------------------------
  function createMenuItemEl(item) {
    const { variantId, title, image, price, quantity } = item;
    const displayPrice = formatPrice(price);

    const li = document.createElement("li");
    li.className = "c-selected-menu__item";
    li.dataset.variantId = variantId;

    li.innerHTML = `
      <img class="c-selected-menu__image" src="${image}" alt="${title}" width="80" height="80">
      <div class="c-selected-menu__detail">
        <p class="c-selected-menu__title">${title}</p>
        <div class="c-selected-menu__bottom">
          <span class="c-selected-menu__price">${displayPrice}</span>
          <div class="c-selected-menu__quantity-wrap">
            <button class="c-quantity__button quantity__button c-selected-menu__qty-btn" data-action="minus" aria-label="${title}の数量を減らす" type="button">
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2">
                  <path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"/>
                </svg>
              </span>
            </button>
            <input class="c-quantity__input quantity__input c-selected-menu__qty-input" type="number" value="${quantity}" min="1" step="1" readonly aria-label="${title}の数量">
            <button class="c-quantity__button quantity__button c-selected-menu__qty-btn" data-action="plus" aria-label="${title}の数量を増やす" type="button">
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10">
                  <path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/>
                </svg>
              </span>
            </button>
            <button class="c-selected-menu__delete-btn" data-action="delete" aria-label="${title}を削除" type="button">
              <span class="svg-wrapper">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.09578 8.76331C1.09578 9.02423 1.13136 9.2661 1.21139 9.46817C1.3199 9.73544 1.50672 9.92794 1.78778 9.98361C1.84472 9.99317 1.90343 9.99952 1.96567 9.99952H8.04453C8.06053 9.99952 8.0748 9.99952 8.0872 9.99792C8.66718 9.96454 8.90377 9.4252 8.90377 8.76331L9.14751 3.48438H0.852051L1.09578 8.76331ZM6.65513 4.66171H7.8097V8.82219H6.65513V4.66171ZM4.42247 4.66171H5.57708V8.82219H4.42247V4.66171ZM2.18986 4.66171H3.34622V8.82219H2.18986V4.66171Z" fill="currentColor"/><path d="M7.47858 1.75191C7.4803 1.71212 7.48391 1.67227 7.48391 1.63242V1.52259C7.48391 0.683282 6.71785 0 5.77676 0H4.22138C3.28029 0 2.51422 0.683262 2.51422 1.52259V1.63242C2.51422 1.67227 2.51779 1.71212 2.52139 1.75191H0V2.9798H10V1.75191H7.47858ZM6.41247 1.63242C6.41247 1.67388 6.40715 1.71367 6.39822 1.75191H3.59998C3.59105 1.71367 3.58568 1.67388 3.58568 1.63242V1.52259C3.58568 1.21042 3.87137 0.955594 4.2214 0.955594H5.77678C6.12855 0.955594 6.41247 1.21042 6.41247 1.52259V1.63242Z" fill="currentColor"/></svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    `;

    return li;
  }

  // -----------------------------------------------------------------------
  // 選択中メニューUIを全リスト再描画
  // -----------------------------------------------------------------------
  function renderSelectedMenu(selections) {
    document.querySelectorAll(".c-selected-menu__list").forEach((list) => {
      list.innerHTML = "";
      if (Object.keys(selections).length === 0) {
        const empty = document.createElement("p");
        empty.className = "c-selected-menu__empty";
        empty.textContent = "商品を選択してください";
        list.appendChild(empty);
        return;
      }
      Object.values(selections).forEach((item) => {
        list.appendChild(createMenuItemEl(item));
      });
    });
  }

  // -----------------------------------------------------------------------
  // 空メッセージの表示・非表示（全リスト対象）
  // -----------------------------------------------------------------------
  function updateEmptyMessage(selections) {
    document.querySelectorAll(".c-selected-menu__list").forEach((list) => {
      const existing = list.querySelector(".c-selected-menu__empty");
      if (Object.keys(selections).length === 0) {
        if (!existing) {
          const empty = document.createElement("p");
          empty.className = "c-selected-menu__empty";
          empty.textContent = "商品を選択してください";
          list.appendChild(empty);
        }
      } else {
        if (existing) existing.remove();
      }
    });
  }

  // -----------------------------------------------------------------------
  // 選択中メニュー内ボタンのイベント（イベント委譲・一度だけ登録）
  // -----------------------------------------------------------------------
  function bindMenuItemEvents(list) {
    list.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-action]");
      if (!btn) return;

      const li = btn.closest(".c-selected-menu__item");
      if (!li) return;
      const variantId = li.dataset.variantId;
      const selections = loadSelections();
      if (!selections[variantId]) return;

      const action = btn.dataset.action;

      if (action === "plus") {
        selections[variantId].quantity += 1;
        saveSelections(selections);
        document
          .querySelectorAll(
            `.c-selected-menu__item[data-variant-id="${variantId}"]`,
          )
          .forEach((el) => {
            el.querySelector(".c-selected-menu__qty-input").value =
              selections[variantId].quantity;
          });
      } else if (action === "minus") {
        selections[variantId].quantity -= 1;
        if (selections[variantId].quantity <= 0) {
          delete selections[variantId];
          document
            .querySelectorAll(
              `.c-selected-menu__item[data-variant-id="${variantId}"]`,
            )
            .forEach((el) => el.remove());
          syncSelectButtons(selections);
          updateEmptyMessage(selections);
        } else {
          document
            .querySelectorAll(
              `.c-selected-menu__item[data-variant-id="${variantId}"]`,
            )
            .forEach((el) => {
              el.querySelector(".c-selected-menu__qty-input").value =
                selections[variantId].quantity;
            });
        }
        saveSelections(selections);
      } else if (action === "delete") {
        delete selections[variantId];
        saveSelections(selections);
        document
          .querySelectorAll(
            `.c-selected-menu__item[data-variant-id="${variantId}"]`,
          )
          .forEach((el) => el.remove());
        syncSelectButtons(selections);
        updateEmptyMessage(selections);
        clearReflectNotice();
      }

      updateCategoryStatus(selections);
      updateFixedArea(selections);
    });
  }

  // -----------------------------------------------------------------------
  // 「前回の内容を反映」注記を削除
  // -----------------------------------------------------------------------
  function clearReflectNotice() {
    document
      .querySelectorAll(".js-reflect-last-order-notice")
      .forEach((el) => el.remove());
  }

  // -----------------------------------------------------------------------
  // 「選択する」ボタンのクリック処理（カード上ボタン）
  // -----------------------------------------------------------------------
  function bindSelectButtons() {
    document
      .querySelectorAll(".c-product-card__select-button[data-variant-id]")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;

          const li = btn.closest("li[data-variant-id]");
          if (!li) return;

          const variantId = li.dataset.variantId;
          const selections = loadSelections();
          if (selections[variantId]) {
            // 選択済みのボタンをもう一度押したら選択解除（横田指摘）
            removeSelection(variantId);
          } else {
            clearReflectNotice();
            addSelection(li, 1);
          }
        });
      });
  }

  // -----------------------------------------------------------------------
  // 選択処理の共通化（カードボタン・モーダルボタン共用）
  // -----------------------------------------------------------------------
  function addSelection(li, quantity) {
    const variantId = li.dataset.variantId;
    const productId = li.dataset.productId;
    const title = li.dataset.productTitle;
    const image = li.dataset.productImage;
    const price = parseInt(li.dataset.price, 10);
    const compareAt = parseInt(li.dataset.compareAtPrice, 10) || 0;
    const categories = li.dataset.categories
      ? li.dataset.categories
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : [];

    const selections = loadSelections();
    if (selections[variantId]) return;

    selections[variantId] = {
      variantId,
      productId,
      title,
      image,
      price,
      compareAtPrice: compareAt > 0 ? compareAt : null,
      categories,
      quantity,
    };

    saveSelections(selections);

    document.querySelectorAll(".c-selected-menu__list").forEach((list) => {
      list.appendChild(createMenuItemEl(selections[variantId]));
    });
    updateEmptyMessage(selections);
    syncSelectButtons(selections);
    updateCategoryStatus(selections);
    updateFixedArea(selections);
  }

  // -----------------------------------------------------------------------
  // 選択解除（カード上の選択済みボタン再クリック用）
  // -----------------------------------------------------------------------
  function removeSelection(variantId) {
    const selections = loadSelections();
    if (!selections[variantId]) return;

    delete selections[variantId];
    saveSelections(selections);

    document
      .querySelectorAll(
        `.c-selected-menu__item[data-variant-id="${variantId}"]`,
      )
      .forEach((el) => el.remove());
    syncSelectButtons(selections);
    updateEmptyMessage(selections);
    updateCategoryStatus(selections);
    updateFixedArea(selections);
    clearReflectNotice();
  }

  // -----------------------------------------------------------------------
  // 「カートに追加」ボタンの処理
  // -----------------------------------------------------------------------
  function bindCartButton() {
    document.querySelectorAll(".js-choice-cart-button").forEach((btn) => {
      btn.addEventListener("click", execCartAdd);
    });
  }

  // -----------------------------------------------------------------------
  // カート追加の実処理（共通）
  // -----------------------------------------------------------------------
  async function execCartAdd() {
    const selections = loadSelections();
    const items = Object.values(selections);
    if (items.length === 0) return;

    document.querySelectorAll(".js-choice-cart-button").forEach((btn) => {
      btn.disabled = true;
    });
    document.querySelectorAll(".js-choice-cart-button__text").forEach((el) => {
      el.hidden = true;
    });
    document
      .querySelectorAll(".js-choice-cart-button .svg-wrapper")
      .forEach((el) => {
        el.hidden = true;
      });
    document
      .querySelectorAll(".js-choice-cart-button__spinner .hidden")
      .forEach((el) => {
        el.classList.remove("hidden");
      });

    try {
      const cartDrawer = document.querySelector("cart-drawer");
      const cartNotification = document.querySelector("cart-notification");
      const cartComponent = cartDrawer || cartNotification;

      let sectionsParam = "";
      if (
        cartComponent &&
        typeof cartComponent.getSectionsToRender === "function"
      ) {
        sectionsParam = cartComponent
          .getSectionsToRender()
          .map((s) => s.section || s.id)
          .join(",");
      }

      // 全商品を items 配列で一括送信（1リクエスト）
      const cartItems = items.map((item) => ({
        id: item.variantId,
        quantity: item.quantity,
      }));

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cartItems,
          sections: sectionsParam || "cart-notification,cart-icon-bubble",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || "カートへの追加に失敗しました");
      }

      const lastData = await response.json();

      // カート追加直前の選択データをスナップショットとして保存（ポップアップ表示用）
      sessionStorage.setItem(
        SNAPSHOT_KEY,
        sessionStorage.getItem(SESSION_KEY) || "{}",
      );

      if (cartComponent && typeof cartComponent.renderContents === "function") {
        // 一括送信レスポンスには key がないため、最後のアイテムの key を手動でセット
        const lastItem = lastData.items?.[lastData.items.length - 1];
        if (lastItem?.key) lastData.key = lastItem.key;

        const productArea = document.getElementById(
          "cart-notification-product",
        );
        if (productArea) {
          const obs = new MutationObserver(() => {
            obs.disconnect();
            renderChoiceNotification();
          });
          obs.observe(productArea, { childList: true, subtree: true });
        }
        cartComponent.renderContents(lastData);
      } else if (!cartComponent) {
        window.location.href = window.Shopify?.routes?.cart_url ?? "/cart";
        return;
      }

      sessionStorage.removeItem(SESSION_KEY);
      renderSelectedMenu({});
      syncSelectButtons({});
      updateCategoryStatus({});
      updateFixedArea({});
    } catch (error) {
      console.error(error);
      alert(
        error.message ||
          "カートへの追加に失敗しました。もう一度お試しください。",
      );
      updateFixedArea(loadSelections());
    } finally {
      const sel = loadSelections();
      const finalText =
        Object.keys(sel).length > 0
          ? "カートに追加する"
          : "商品を選択してください";
      document
        .querySelectorAll(".js-choice-cart-button__text")
        .forEach((el) => {
          el.textContent = finalText;
          el.hidden = false;
        });
      document
        .querySelectorAll(".js-choice-cart-button__spinner .loading__spinner")
        .forEach((el) => {
          el.classList.add("hidden");
        });
      document
        .querySelectorAll(".js-choice-cart-button .svg-wrapper")
        .forEach((el) => {
          el.hidden = false;
        });
    }
  }

  // -----------------------------------------------------------------------
  // チョイスセット専用 カート追加後ポップアップ描画
  // -----------------------------------------------------------------------
  function renderChoiceNotification() {
    const productArea = document.getElementById("cart-notification-product");
    if (!productArea) return;

    let snapshot = {};
    try {
      snapshot = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || "{}");
    } catch {
      snapshot = {};
    }

    const items = Object.values(snapshot);
    if (items.length === 0) return;

    const totalCents = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    const totalYen = Math.floor(totalCents / 100).toLocaleString("ja-JP");

    const categoryOrder = ["主菜", "副主菜", "副菜", "おやつ"];
    const categoryLabels = {
      主菜: "主　菜",
      副主菜: "副主菜",
      副菜: "副　菜",
      おやつ: "おやつ",
    };
    const categoryMap = {};
    categoryOrder.forEach((cat) => {
      categoryMap[cat] = [];
    });

    items.forEach((item) => {
      item.categories.forEach((rawCat) => {
        const displayCat = CATEGORY_MAP[rawCat];
        if (displayCat && categoryMap[displayCat] !== undefined) {
          const label =
            item.quantity > 1 ? `${item.title} × ${item.quantity}` : item.title;
          if (
            !categoryMap[displayCat].find((e) => e.variantId === item.variantId)
          ) {
            categoryMap[displayCat].push({ variantId: item.variantId, label });
          }
        }
      });
    });

    const categoriesHtml = categoryOrder
      .filter((cat) => categoryMap[cat].length > 0)
      .map((cat) => {
        const itemsText = categoryMap[cat].map((e) => e.label).join(" / ");
        return `<div class="c-cart-notification-choice__category-row">
          <span class="c-cart-notification-choice__category-label">${categoryLabels[cat]}:</span>
          <span class="c-cart-notification-choice__category-items">${itemsText}</span>
        </div>`;
      })
      .join("");

    const collectionImageSrc = window.CHOICE_SET_COLLECTION_IMAGE || "";
    const imageHtml = collectionImageSrc
      ? `<img
              src="${collectionImageSrc}"
              alt="自分でチョイスセット"
              width="71"
              height="53"
              loading="lazy"
            >`
      : "";

    productArea.innerHTML = `
      <div class="c-cart-notification-choice">
        <div class="c-cart-notification-choice__product">
          <div class="c-cart-notification-product__image">
            ${imageHtml}
          </div>
          <div class="c-cart-notification-choice__info">
            <h3 class="c-cart-notification-product__name">自分でチョイスセット</h3>
            <p class="c-cart-notification-choice__total"><span class="c-cart-notification-choice__total-text">合計</span> ¥${totalYen}</p>
          </div>
        </div>
        <div class="c-cart-notification-choice__categories">${categoriesHtml}</div>
      </div>
    `;
  }

  // =======================================================================
  // ▼▼▼ 商品詳細モーダル ▼▼▼
  // =======================================================================

  // モーダルのDOM要素（初期化時に生成）
  let modalEl = null;
  let overlayEl = null;
  let currentSplideMain = null;
  let currentSplideThumbs = null;

  // -----------------------------------------------------------------------
  // モーダルのDOM生成（一度だけ）
  // -----------------------------------------------------------------------
  function createModalDOM() {
    // オーバーレイ（背景暗転 + 外クリックで閉じる）
    overlayEl = document.createElement("div");
    overlayEl.className = "c-choice-product-modal-overlay";
    overlayEl.setAttribute("aria-hidden", "true");
    overlayEl.addEventListener("click", closeModal);

    // モーダルラッパー（relative基準。閉じるボタンをここからはみ出させる）
    const wrapperEl = document.createElement("div");
    wrapperEl.className = "c-choice-product-modal__wrapper";

    // 閉じるボタン（ラッパー右上に absolute で配置）
    const closeBtnEl = document.createElement("button");
    closeBtnEl.className =
      "c-choice-product-modal__close-btn js-choice-modal-close";
    closeBtnEl.type = "button";
    closeBtnEl.setAttribute("aria-label", "モーダルを閉じる");
    closeBtnEl.innerHTML = `<span class="svg-wrapper"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>`;
    closeBtnEl.addEventListener("click", closeModal);

    // モーダル本体
    modalEl = document.createElement("div");
    modalEl.className = "c-choice-product-modal";
    modalEl.setAttribute("role", "dialog");
    modalEl.setAttribute("aria-modal", "true");
    modalEl.setAttribute("aria-label", "商品詳細");
    modalEl.innerHTML = `<div class="c-choice-modal__body" id="js-choice-modal-body"></div>`;

    wrapperEl.appendChild(closeBtnEl);
    wrapperEl.appendChild(modalEl);

    document.body.appendChild(overlayEl);
    document.body.appendChild(wrapperEl);

    // Escキーで閉じる
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modalEl.classList.contains("is-open")) {
        closeModal();
      }
    });
  }

  // -----------------------------------------------------------------------
  // モーダルを開く
  // -----------------------------------------------------------------------
  function openModal(cardLi) {
    const template = cardLi.querySelector(".js-choice-product-modal-data");
    if (!template) return;

    const body = modalEl.querySelector("#js-choice-modal-body");

    // 前回のSplideを破棄
    if (currentSplideMain) {
      try {
        currentSplideMain.destroy();
      } catch (_) {}
      currentSplideMain = null;
    }
    if (currentSplideThumbs) {
      try {
        currentSplideThumbs.destroy();
      } catch (_) {}
      currentSplideThumbs = null;
    }

    // templateの中身をクローンして差し込む
    body.innerHTML = "";
    const clone = template.content.cloneNode(true);
    body.appendChild(clone);

    // 選択済み状態をモーダルに反映
    const variantId = cardLi.dataset.variantId;
    const selections = loadSelections();
    const isSelected = !!selections[variantId];
    const qtyInput = body.querySelector(".js-choice-modal-qty-input");
    const selectBtn = body.querySelector(".js-choice-modal-select-btn");

    if (isSelected && qtyInput) {
      // 現在の選択数量を初期表示
      const currentQty = selections[variantId].quantity;
      qtyInput.value = currentQty;
      // 数量が2以上ならマイナスボタンを活性化
      const minusBtn = body.querySelector(
        ".js-choice-modal-qty-btn[data-action='minus']",
      );
      if (minusBtn) minusBtn.disabled = currentQty <= 1;
    }
    if (selectBtn && !selectBtn.disabled) {
      if (isSelected) {
        // 「数量を更新する」ボタンに変更（常に活性・オレンジ）
        const textEl = selectBtn.querySelector(
          ".js-choice-modal-select-btn-text",
        );
        if (textEl) textEl.textContent = "数量を更新する";
      }
    }

    // 数量ボタンのイベント
    bindModalQtyButtons(body);

    // 選択ボタンのイベント
    bindModalSelectButton(body, cardLi, isSelected);

    // Splideの初期化
    initModalSplide(body);

    // 表示
    overlayEl.classList.add("is-open");
    modalEl.classList.add("is-open");
    const wrapperEl = modalEl.closest(".c-choice-product-modal__wrapper");
    if (wrapperEl) wrapperEl.classList.add("is-open");
    modalEl.setAttribute("aria-hidden", "false");
    overlayEl.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    // フォーカス管理
    const closeBtnEl = document.querySelector(".js-choice-modal-close");
    if (closeBtnEl) closeBtnEl.focus();
  }

  // -----------------------------------------------------------------------
  // モーダルを閉じる
  // -----------------------------------------------------------------------
  function closeModal() {
    overlayEl.classList.remove("is-open");
    modalEl.classList.remove("is-open");
    const wrapperEl = modalEl.closest(".c-choice-product-modal__wrapper");
    if (wrapperEl) wrapperEl.classList.remove("is-open");
    modalEl.setAttribute("aria-hidden", "true");
    overlayEl.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }

  // -----------------------------------------------------------------------
  // モーダル内 数量ボタン
  // -----------------------------------------------------------------------
  function bindModalQtyButtons(body) {
    const qtyInput = body.querySelector(".js-choice-modal-qty-input");
    const minusBtn = body.querySelector(
      ".js-choice-modal-qty-btn[data-action='minus']",
    );

    body.querySelectorAll(".js-choice-modal-qty-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!qtyInput) return;
        let val = parseInt(qtyInput.value, 10) || 1;
        if (btn.dataset.action === "plus") {
          val += 1;
        } else if (btn.dataset.action === "minus") {
          val = Math.max(1, val - 1);
        }
        qtyInput.value = val;

        // 数量が1のときマイナスボタンをdisabled
        if (minusBtn) minusBtn.disabled = val <= 1;
      });
    });
  }

  // -----------------------------------------------------------------------
  // モーダル内 選択ボタン
  // -----------------------------------------------------------------------
  function bindModalSelectButton(body, cardLi, isAlreadySelected) {
    const selectBtn = body.querySelector(".js-choice-modal-select-btn");
    if (!selectBtn) return;

    selectBtn.addEventListener("click", () => {
      if (selectBtn.disabled) return;

      const qtyInput = body.querySelector(".js-choice-modal-qty-input");
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      const variantId = cardLi.dataset.variantId;

      if (isAlreadySelected) {
        // 数量を更新する
        const selections = loadSelections();
        if (!selections[variantId]) return;

        selections[variantId].quantity = quantity;
        saveSelections(selections);

        // サイドバーの数量表示を同期
        document
          .querySelectorAll(
            `.c-selected-menu__item[data-variant-id="${variantId}"]`,
          )
          .forEach((el) => {
            const input = el.querySelector(".c-selected-menu__qty-input");
            if (input) input.value = quantity;
          });

        updateCategoryStatus(selections);
        updateFixedArea(selections);
      } else {
        // 新規選択
        clearReflectNotice();
        addSelection(cardLi, quantity);
      }

      closeModal();
    });
  }

  // -----------------------------------------------------------------------
  // モーダル内 Splide 初期化
  // -----------------------------------------------------------------------
  function initModalSplide(body) {
    const mainEl = body.querySelector(".js-choice-modal-main");
    const thumbsEl = body.querySelector(".js-choice-modal-thumbnails");

    if (!mainEl) return;

    // Splideが読み込まれていない場合はスキップ
    if (typeof Splide === "undefined") return;

    if (thumbsEl) {
      currentSplideMain = new Splide(mainEl, {
        type: "fade",
        rewind: true,
        pagination: false,
        arrows: false,
      });
      currentSplideThumbs = new Splide(thumbsEl, {
        perPage: 4,
        perMove: 1,
        gap: 8,
        pagination: false,
        isNavigation: true,
        arrows: false,
        breakpoints: {
          750: { gap: 8 },
        },
      });
      currentSplideMain.sync(currentSplideThumbs);
      currentSplideMain.mount();
      currentSplideThumbs.mount();
    } else {
      // 画像が1枚の場合はサムネイルなし
      currentSplideMain = new Splide(mainEl, {
        type: "fade",
        rewind: true,
        pagination: false,
        arrows: false,
      });
      currentSplideMain.mount();
    }
  }

  // -----------------------------------------------------------------------
  // カードトリガーのイベントバインド（.js-choice-card 限定）
  // -----------------------------------------------------------------------
  function bindCardTriggers() {
    document
      .querySelectorAll(".js-choice-card .js-choice-card-trigger")
      .forEach((trigger) => {
        trigger.addEventListener("click", () => {
          const cardLi = trigger.closest(".js-choice-card");
          if (!cardLi) return;
          openModal(cardLi);
        });
      });
  }

  // =======================================================================
  // ▲▲▲ 商品詳細モーダル ▲▲▲
  // =======================================================================

  // -----------------------------------------------------------------------
  // 「前回の内容を反映」処理
  // -----------------------------------------------------------------------
  function reflectLastOrder() {
    const lastOrderItems = window.__lastOrderItems__;
    if (!lastOrderItems || lastOrderItems.length === 0) return;

    // ページ上に存在する、かつ在庫ありのvariantIdをSetとして収集
    const availableVariantIds = new Set();
    document
      .querySelectorAll(".js-choice-card[data-variant-id]")
      .forEach((card) => {
        if (card.dataset.available === "true") {
          availableVariantIds.add(String(card.dataset.variantId));
        }
      });

    // sessionStorageをクリアして前回注文を反映
    const newSelections = {};
    const skippedItems = [];

    lastOrderItems.forEach((item) => {
      const variantId = String(item.variantId);
      if (availableVariantIds.has(variantId)) {
        newSelections[variantId] = {
          variantId,
          productId: String(item.productId),
          title: item.title,
          image: item.image,
          price: item.price,
          compareAtPrice: item.compareAtPrice,
          categories: Array.isArray(item.categories) ? item.categories : [],
          quantity: item.quantity,
        };
      } else {
        skippedItems.push(item.title);
      }
    });

    saveSelections(newSelections);
    renderSelectedMenu(newSelections);
    syncSelectButtons(newSelections);
    updateCategoryStatus(newSelections);
    updateFixedArea(newSelections);

    // 取り扱いなし・在庫切れ商品がある場合は注記を表示
    clearReflectNotice();
    if (skippedItems.length > 0) {
      document.querySelectorAll(".js-reflect-last-order").forEach((btn) => {
        const notice = document.createElement("div");
        notice.className =
          "c-reflect-last-order__skipped-notice js-reflect-last-order-notice";
        const heading = document.createElement("p");
        heading.textContent =
          "※以下の商品は現在取り扱いがないため反映されませんでした";
        const list = document.createElement("ul");
        list.className = "c-reflect-last-order__skipped-list";
        skippedItems.forEach((title) => {
          const li = document.createElement("li");
          li.textContent = title;
          list.appendChild(li);
        });
        notice.appendChild(heading);
        notice.appendChild(list);
        btn.insertAdjacentElement("afterend", notice);
      });
    }
  }

  // -----------------------------------------------------------------------
  // 「前回の内容を反映」ボタンのイベントバインド
  // -----------------------------------------------------------------------
  function bindReflectLastOrderButton() {
    document.querySelectorAll(".js-reflect-last-order").forEach((btn) => {
      btn.addEventListener("click", reflectLastOrder);
    });
  }

  // -----------------------------------------------------------------------
  // 初期化
  // -----------------------------------------------------------------------
  async function init() {
    if (!document.querySelector(".c-selected-menu__list")) return;

    const selections = loadSelections();

    renderSelectedMenu(selections);
    syncSelectButtons(selections);
    updateCategoryStatus(selections);
    await updateFixedArea(selections);

    document.querySelectorAll(".c-selected-menu__list").forEach((list) => {
      bindMenuItemEvents(list);
    });
    bindSelectButtons();
    bindCartButton();
    bindReflectLastOrderButton();

    // モーダル初期化
    createModalDOM();
    bindCardTriggers();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
